import axios, { AxiosProgressEvent, AxiosResponse } from 'axios';
import { loadConfig } from '@api/api';
import { filesToFormData } from 'utils/index';
import { CalciumAnalysisResult } from '@models/Parameter';

const inProgressMap = new Map<string, Promise<any>>();
const stripLeadingDotSlash = (p: string): string =>
  p.startsWith('./') ? p.slice(2) : p;
const encodeSeriesTarget = (p: string): string =>
  stripLeadingDotSlash(p).replace(/\//g, '%2F');

const getFilesApi = async () => {
  const BASE_URL = await loadConfig();
  return `${BASE_URL}/api/v1/files`;
};
const getLogApi = async (seriesid: string) => {
  const BASE_URL = await loadConfig();
  return `${BASE_URL}/api/v1/elasticsearch/download/${seriesid}`;
};

export const fetchSeriesJson = async ({
  accessToken,
  seriesId,
  target,
  CacheControl,
}: {
  accessToken: string;
  seriesId: string;
  target: string;
  CacheControl?: any;
}): Promise<CalciumAnalysisResult> => {
  const FILE_API = await getFilesApi();

  const fileName = target.endsWith('.json') ? target : `${target}.json`;

  const url = `${FILE_API}/${seriesId}%2F${fileName}`;

  const res = await axios.get<CalciumAnalysisResult>(url, {
    headers: {
      Accept: '*/*',
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
      'Cache-Control': 'max-age=3600',
      ...CacheControl,
    },
  });

  return res.data;
};

export const fetchSeries3DModel = ({
  accessToken,
  seriesId,
  target,
}: {
  accessToken: string;
  seriesId: string;
  target: string;
}): Promise<ArrayBuffer | null> => {
  target = encodeSeriesTarget(target);

  return getFilesApi()
    .then((FILE_API) =>
      axios.get(`${FILE_API}/${seriesId}%2F${target}`, {
        headers: {
          Accept: '*/*',
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/octet-stream',
          Pragma: 'no-cache',
          Expires: '0',
        },
        responseType: 'arraybuffer',
      }),
    )
    .then((res) => (res.data?.byteLength ? res.data : null))
    .catch((err) => {
      if (axios.isAxiosError(err) && err.response?.status === 404) {
        console.warn(`[3D Moel] 파일 없음: ${target}`);
      } else {
        console.error(`[3D Moel] 다운로드 실패: ${target}`, err);
      }
      return null;
    });
};

export const fetchSeriesDicomTags = async ({
  accessToken,
  seriesId,
}: {
  accessToken: string;
  seriesId: string;
}) => {
  const FILE_API = await getFilesApi();

  const res: AxiosResponse = await axios({
    method: 'get',
    url: `${FILE_API}/${seriesId}%2Fparameter_raw.json`,
    headers: {
      Accept: '*/*',
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
  });

  return res;
};

export const fetchSeriesArrayBuffer = async ({
  accessToken,
  seriesId,
  target,
  onDownloadProgress,
}: {
  accessToken: string;
  seriesId: string;
  target: string;
  onDownloadProgress?: (progressEvent: AxiosProgressEvent) => void;
}) => {
  const requestKey = `${seriesId}:${target}`;
  if (inProgressMap.has(requestKey)) return inProgressMap.get(requestKey);

  const FILE_API = await getFilesApi();

  const req = axios({
    method: 'get',
    url: `${FILE_API}/${seriesId}%2F${target}`,
    headers: {
      Accept: '*/*',
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/octet-stream',
      'Cache-Control': 'max-age=3600',
      Pragma: 'no-cache',
      Expires: '0',
    },
    responseType: 'arraybuffer',
    timeout: 150000,
    onDownloadProgress,
  })
    .then((res) => {
      return res;
    })
    .catch((err) => {
      return err;
    })
    .finally(() => {
      inProgressMap.delete(requestKey);
    });
  inProgressMap.set(requestKey, req);

  return req;
};

export const downloadNiftiSeries = async ({
  accessToken,
  seriesId,
  isPreview,
}: {
  accessToken: string;
  seriesId: string;
  isPreview: boolean;
}) => {
  const FILE_API = await getFilesApi();
  const fileName = `${seriesId}${isPreview ? '_preview' : ''}.nii.gz`;

  const res: AxiosResponse = await axios({
    method: 'get',
    url: `${FILE_API}/${seriesId}%2F${fileName}`,
    headers: {
      Accept: '*/*',
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/octet-stream',
    },
    responseType: 'blob',
  });

  const blobUrl = URL.createObjectURL(res.data);

  return blobUrl;
};

export const uploadFiles = async ({
  accessToken,
  files,
}: {
  accessToken: string;
  files: File[];
}) => {
  if (!accessToken) {
    throw new Error('uploadFiles: accessToken is required');
  }
  if (!Array.isArray(files) || files.length === 0) {
    throw new Error('uploadFiles: files must be a non-empty array');
  }

  const FILE_API = await getFilesApi();
  const BASE_URL = await loadConfig();
  if (!BASE_URL) {
    throw new Error('uploadFiles: failed to load API base url');
  }
  const formData = filesToFormData(files);

  const uploadURL: AxiosResponse = await axios(`${FILE_API}/upload-url`, {
    method: 'get',
    headers: {
      Accept: '*/*',
      Authorization: `Bearer ${accessToken}`,
    },
  });

  const uploadUrlFromApi = uploadURL?.data?.uploadUrl;
  const fallbackUrl = `${FILE_API}/upload`;

  const resolvedUrl =
    typeof uploadUrlFromApi === 'string' && uploadUrlFromApi.length > 0
      ? new URL(uploadUrlFromApi, BASE_URL).toString()
      : fallbackUrl;

  const res: AxiosResponse = await axios({
    method: 'post',
    url: resolvedUrl,
    headers: {
      Accept: '*/*',
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'multipart/form-data',
    },
    data: formData,
  });

  return res;
};

export const checkSeriesTargetExists = async ({
  accessToken,
  seriesId,
  target,
}: {
  accessToken: string;
  seriesId: string;
  target: string;
}) => {
  const FILE_API = await getFilesApi();
  const encodedTarget = encodeSeriesTarget(target);

  const res = await axios({
    method: 'get',
    url: `${FILE_API}/${seriesId}%2F${encodedTarget}`,
    headers: {
      Accept: '*/*',
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/octet-stream',
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache',
      Expires: '0',
    },
    responseType: 'arraybuffer',
    validateStatus: (status) => status === 200 || status === 404,
  });

  return res.status === 200;
};

export const fetchSeriesTargetEx = async ({
  accessToken,
  seriesId,
  target,
  res,
}: {
  accessToken: string;
  seriesId: string;
  target: string;
  res?: any;
}) => {
  const FILE_API = await getFilesApi();
  target = encodeSeriesTarget(target);

  const url = `${FILE_API}/${seriesId}%2F${target}`;

  return await axios({
    method: 'get',
    url: url,
    headers: {
      Accept: '*/*',
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/octet-stream',
      ...res,
    },
    responseType: 'arraybuffer',
  });

  // return res.data;
};

export const fetchSeriesLog = async (accessToken: string, seriesid: string) => {
  const url = await getLogApi(seriesid);

  return await axios({
    method: 'get',
    url: url,
    headers: {
      Accept: '*/*',
      Authorization: `Bearer ${accessToken}`,
    },
    timeout: 30000,
    responseType: 'blob',
  });
};
